import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MapPin, ArrowUpRight } from "lucide-react";
import { detectCommunity, communityLabel } from "@/lib/communities";
import { fmtEUR } from "@/lib/format";

// Stylised geographic layout of Spain — each community placed in an
// approximated position so the user gets a "heat-map" feel without needing a
// full topojson dependency. Coordinates are column×row in a 7×5 grid.
const LAYOUT = [
    { key: "galicia", col: 0, row: 0 },
    { key: "asturias", col: 1, row: 0 },
    { key: "cantabria", col: 2, row: 0 },
    { key: "pais_vasco", col: 3, row: 0 },
    { key: "navarra", col: 4, row: 0 },
    { key: "la_rioja", col: 3, row: 1 },
    { key: "aragon", col: 4, row: 1 },
    { key: "cataluna", col: 5, row: 1 },
    { key: "castilla_y_leon", col: 1, row: 1 },
    { key: "madrid", col: 2, row: 2 },
    { key: "castilla_la_mancha", col: 3, row: 2 },
    { key: "valencia", col: 5, row: 2 },
    { key: "baleares", col: 6, row: 2 },
    { key: "extremadura", col: 1, row: 3 },
    { key: "andalucia", col: 2, row: 3, span: 2 },
    { key: "murcia", col: 4, row: 3 },
    { key: "ceuta", col: 1, row: 4 },
    { key: "melilla", col: 2, row: 4 },
    { key: "canarias", col: 0, row: 4 },
    { key: "estatal", col: 0, row: 2 },
];

function intensityClass(intensity) {
    // 0..1 -> tailwind tone
    if (intensity === 0) return "bg-slate-50 border-slate-200 text-slate-400";
    if (intensity < 0.25) return "bg-brand-50 border-brand-200 text-brand-700";
    if (intensity < 0.5) return "bg-brand-100 border-brand-300 text-brand-800";
    if (intensity < 0.75) return "bg-brand-600 border-brand-700 text-white";
    return "bg-ink-950 border-ink-950 text-white";
}

export function SpainHeatMap({ tenders }) {
    const navigate = useNavigate();

    const stats = useMemo(() => {
        const byCommunity = new Map();
        for (const t of tenders) {
            const c = detectCommunity(t);
            if (!c) continue;
            const cur = byCommunity.get(c.key) || { count: 0, budget: 0 };
            cur.count += 1;
            cur.budget += t.budget || 0;
            byCommunity.set(c.key, cur);
        }
        const maxCount = Math.max(0, ...Array.from(byCommunity.values()).map((v) => v.count));
        const topEntry =
            Array.from(byCommunity.entries()).sort((a, b) => b[1].count - a[1].count)[0] || null;
        const totalBudget = Array.from(byCommunity.values()).reduce((acc, v) => acc + v.budget, 0);
        return { byCommunity, maxCount, topEntry, totalBudget };
    }, [tenders]);

    const cols = 7;
    const rows = 5;

    if (!tenders || tenders.length === 0) return null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white border border-ink-100 rounded-2xl p-6 md:p-7 overflow-hidden"
            data-testid="spain-heatmap"
        >
            <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-ink-500 mb-1.5 flex items-center gap-2">
                        <MapPin size={11} />
                        Mapa de actividad
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight text-ink-950 leading-none">
                        {stats.byCommunity.size > 0 ? (
                            <>
                                Operas en{" "}
                                <span className="text-brand-600">
                                    {stats.byCommunity.size}
                                </span>{" "}
                                comunidad{stats.byCommunity.size > 1 ? "es" : ""}
                            </>
                        ) : (
                            "España"
                        )}
                    </h2>
                    {stats.topEntry && (
                        <p className="text-sm text-ink-500 mt-2 font-medium">
                            Comunidad líder:{" "}
                            <span className="text-ink-950 font-bold">
                                {communityLabel(stats.topEntry[0])}
                            </span>{" "}
                            con {stats.topEntry[1].count} licitación
                            {stats.topEntry[1].count > 1 ? "es" : ""}{" "}
                            <span className="text-ink-400">
                                ({fmtEUR(stats.topEntry[1].budget)})
                            </span>
                        </p>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => navigate("/app/licitaciones")}
                    className="text-xs font-bold text-ink-500 hover:text-brand-600 flex items-center gap-1 rounded-xl px-3 py-1.5 hover:bg-brand-50 transition-colors"
                    data-testid="heatmap-see-all"
                >
                    Ver todas
                    <ArrowUpRight size={12} />
                </button>
            </div>

            <div
                className="grid gap-1.5"
                style={{
                    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                    gridTemplateRows: `repeat(${rows}, 60px)`,
                }}
            >
                {LAYOUT.map((cell, i) => {
                    const data = stats.byCommunity.get(cell.key) || { count: 0, budget: 0 };
                    const intensity =
                        stats.maxCount > 0 ? data.count / stats.maxCount : 0;
                    const cls = intensityClass(intensity);
                    const label = communityLabel(cell.key);
                    return (
                        <motion.button
                            type="button"
                            key={cell.key}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{
                                duration: 0.35,
                                delay: i * 0.025,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            whileHover={{ scale: 1.04, y: -2 }}
                            onClick={() => navigate("/app/licitaciones")}
                            style={{
                                gridColumn: `${cell.col + 1} / span ${cell.span || 1}`,
                                gridRow: cell.row + 1,
                            }}
                            className={`relative rounded-lg border-2 ${cls} flex flex-col items-start justify-between p-2 transition-all duration-300 ${
                                data.count > 0 ? "cursor-pointer shadow-pop hover:shadow-pop-lg" : "cursor-default opacity-70"
                            }`}
                            data-testid={`heatmap-cell-${cell.key}`}
                            disabled={data.count === 0}
                            title={`${label}: ${data.count} licitación(es) · ${fmtEUR(data.budget)}`}
                        >
                            <div className="text-[9px] font-black uppercase tracking-wider leading-tight w-full text-left line-clamp-2">
                                {label}
                            </div>
                            {data.count > 0 && (
                                <div className="font-display font-black text-lg leading-none">
                                    {data.count}
                                </div>
                            )}
                        </motion.button>
                    );
                })}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-3 mt-5 text-[10px] font-bold uppercase tracking-wider text-ink-400">
                <span>Sin actividad</span>
                <div className="flex gap-0.5">
                    <span className="w-4 h-2 rounded-sm bg-slate-50 border border-slate-200" />
                    <span className="w-4 h-2 rounded-sm bg-brand-100 border border-brand-300" />
                    <span className="w-4 h-2 rounded-sm bg-brand-600" />
                    <span className="w-4 h-2 rounded-sm bg-ink-950" />
                </div>
                <span>Más activa</span>
            </div>
        </motion.div>
    );
}

export default SpainHeatMap;
